import React, { PureComponent } from 'react';
import * as Yup from 'yup';
import { Formik, Field, FieldArray } from 'formik';
import Toast from '@hobs/components/Toast';
import Dialog from '@hobs/components/Dialog';
import PropTypes from 'prop-types';
import InputLabel from '@hobs/components/InputLabel';
import NumericInput from '@hobs/components/NumericInput';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Input from '@hobs/components/Input';
import Money from '@hobs/components/Money';
import Dropdown from '@hobs/components/Dropdown';
import DropZone from '@hobs/components/DropZone';
import { MAX_INT32 } from '@hobs/utils/constants';
import Confirm from '@hobs/components/Confirm';

import './ConfirmDeliveryModal.scss';
import Switch from '@hobs/components/Switch';

const initialState = {
  deliveryQuantitiesApprovalRequired: false,
  requestsWithExtraQuantities: []
};

class ConfirmDeliveryModal extends PureComponent {
  state = initialState;

  constructor(props) {
    super(props);

    const { t } = this.props;

    this.validationSchema = Yup.object().shape({
      items: Yup.array().of(
        Yup.object().shape({
          deliveryQuantity: Yup.number()
            .nullable()
            .positive(t('Quantity must be greater than 0'))
            .required(t('Quantity is required')),
          price: Yup.number()
            .nullable()
            .required(t('Price is required'))
            .min(0, t('Price must be greater than 0 or equal to 0')),
          totalAmount: Yup.number()
            .nullable()
            .required(t('Total amount is required'))
            .min(0, t('Total amount must be greater than or equal to 0')),
          comment: Yup.string()
            .nullable()
            .max(1500, t('Comment cannot be longer than 1500 symbols'))
        })
      )
    });
  }

  componentWillUnmount() {
    this.props.clearAttachments();
  }

  async componentDidMount() {
    this.props.clearAttachments();
  }

  dismissDialog = () => {
    const { onClose } = this.props;
    this.resetState();
    onClose();
  };

  resetState = () => {
    this.setState(initialState);
  };

  onSubmit = values => {
    this.validateDeliveryQuantities(values.items);

    if (!this.state.deliveryQuantitiesApprovalRequired) {
      this.submitConfirmDelivery(values);
    }
  };

  submitConfirmDelivery = values => {
    const {
      t,
      attachments,
      confirmDelivery,
      onConfirmDeliverSuccess
    } = this.props;

    confirmDelivery({
      ...values,
      attachments
    }).then(response => {
      if (response.success) {
        onConfirmDeliverSuccess();
        this.dismissDialog();
        Toast.success(t('Service delivery confirmed successfully'));
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  validateDeliveryQuantities = requests => {
    const requestsWithExtraQuantity = requests.filter(
      request => request.deliveryQuantity > request.requestedQuantity
    );

    if (requestsWithExtraQuantity.length > 0) {
      this.setState({
        deliveryQuantitiesApprovalRequired: true,
        requestsWithExtraQuantities: requestsWithExtraQuantity.map(
          req => req.requestNumber
        )
      });
    }
  };

  confirmDeliveryWithExtraQuantities = values => {
    this.resetState();
    this.submitConfirmDelivery(values);
  };

  getRequestWithExtraQuantitiesNumbersForMessage = () => {
    const { requestsWithExtraQuantities } = this.state;
    let text = '';
    if (requestsWithExtraQuantities) {
      text +=
        requestsWithExtraQuantities.length == 1 ? '(Request ' : '(Requests ';
      text += requestsWithExtraQuantities.join(', ');
      text += ')';
    }
    return text;
  };

  getInitialValues() {
    return {
      items: [...this.props.forSubmit.items]
    };
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  renderVatOptions() {
    const { valueAddedTaxes } = this.props.forSubmit;
    return valueAddedTaxes.map(vat => {
      return (
        <option key={vat.valueAddedTaxId} value={vat.valueAddedTaxId}>
          {vat.codeAndPercentage}
        </option>
      );
    });
  }

  getVatPercentage(id) {
    const { valueAddedTaxes } = this.props.forSubmit;

    return valueAddedTaxes.find(vat => vat.valueAddedTaxId === id).percentage;
  }

  calcUnitCost = (canDeliveryQuantity, totalCost, setFieldValue, path) => {
    if (!canDeliveryQuantity) {
      return;
    }
    if (!totalCost) {
      totalCost = 0;
    }
    setFieldValue(
      `${path}.price`,
      (totalCost / canDeliveryQuantity).toFixed(2)
    );
  };

  calcTotalCost = (canDeliveryQuantity, unitCost, setFieldValue, path) => {
    if (!canDeliveryQuantity && canDeliveryQuantity !== 0) {
      canDeliveryQuantity = 0;
    }
    if (!unitCost && unitCost !== 0) {
      unitCost = 0;
    }

    setFieldValue(
      `${path}.totalAmount`,
      (unitCost * canDeliveryQuantity).toFixed(2)
    );
  };

  render() {
    const { isOpen, t, onClose, attachments } = this.props;

    return (
      <Dialog
        title="Confirm service delivery"
        className="confirm-delivery"
        onClose={onClose}
        isOpen={isOpen}
      >
        {isOpen && (
          <div>
            <div>
              <p>
                {t(
                  'Are you sure you want to mark selected service lines as delivered? The following information will be seen in Annex overview'
                )}
                :
              </p>
            </div>
            <Formik
              enableReinitialize={true}
              validationSchema={this.validationSchema}
              initialValues={this.getInitialValues()}
              onSubmit={this.onSubmit}
            >
              {({
                isSubmitting,
                setSubmitting,
                values,
                handleSubmit,
                setFieldValue
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <table>
                      <thead>
                        <tr>
                          <th>{t('Request')}</th>
                          <th>{t('Subcontractor')}</th>
                          <th>{t('Service name')}</th>
                          <th>{t('Delivery q-ty')}</th>
                          {this.props.showCurrency && <th>{t('Currency')}</th>}
                          <th>{t('Price')}</th>
                          <th>{t('Total amount')}</th>
                          <th>{t('VAT')}</th>
                          <th>{t('Gross amount')}</th>
                          <th>{t('Comment')}</th>
                          <th>{t('Delivery finalized')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray
                          name="items"
                          render={() => {
                            return values.items.map((item, index) => {
                              return (
                                <tr key={item.serviceRequestId}>
                                  <td>{item.requestNumber}</td>
                                  <td>{item.subcontractor}</td>
                                  <td>{item.serviceName}</td>
                                  <td className="confirm-delivery--numeric">
                                    <Field
                                      noMargin
                                      name={`items.${index}.deliveryQuantity`}
                                      component={NumericInput}
                                      setFieldValue={setFieldValue}
                                      onChange={event => {
                                        item.price
                                          ? this.calcTotalCost(
                                              event,
                                              item.price,
                                              setFieldValue,
                                              `items.${index}`
                                            )
                                          : this.calcUnitCost(
                                              event,
                                              item.totalAmount,
                                              setFieldValue,
                                              `items.${index}`
                                            );
                                      }}
                                    ></Field>
                                  </td>
                                  {this.props.showCurrency && (
                                    <td>{item.currency}</td>
                                  )}
                                  <td className="confirm-delivery--numeric">
                                    <Field
                                      noMargin
                                      name={`items.${index}.price`}
                                      component={Input}
                                      type="number"
                                      min={0}
                                      setFieldValue={setFieldValue}
                                      onChange={event => {
                                        if (event.target.value > MAX_INT32) {
                                          event.target.value = MAX_INT32;
                                        }
                                        this.calcTotalCost(
                                          item.deliveryQuantity,
                                          event.target.value,
                                          setFieldValue,
                                          `items.${index}`
                                        );
                                      }}
                                    ></Field>
                                  </td>
                                  <td className="confirm-delivery--numeric">
                                    <Field
                                      noMargin
                                      name={`items.${index}.totalAmount`}
                                      component={Input}
                                      type="number"
                                      min={0}
                                      setFieldValue={setFieldValue}
                                      onChange={event => {
                                        if (event.target.value > MAX_INT32) {
                                          event.target.value = MAX_INT32;
                                        }
                                        this.calcUnitCost(
                                          item.deliveryQuantity,
                                          event.target.value,
                                          setFieldValue,
                                          `items.${index}`
                                        );
                                      }}
                                    ></Field>
                                  </td>
                                  <td>
                                    <Field
                                      noMargin
                                      name={`items.${index}.valueAddedTaxId`}
                                      component={Dropdown}
                                      defaultValue={item.valueAddedTaxId}
                                    >
                                      {this.renderVatOptions()}
                                    </Field>
                                  </td>
                                  <td>
                                    <Money
                                      amount={
                                        item.totalAmount *
                                        (this.getVatPercentage(
                                          item.valueAddedTaxId
                                        ) /
                                          100 +
                                          1)
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Field
                                      noMargin
                                      name={`items.${index}.comment`}
                                      placeholder={t('Enter comment')}
                                      component={Input}
                                    ></Field>
                                  </td>
                                  <td className="confirm-delivery--switch">
                                    <Field
                                      component={Switch}
                                      name={`items.${index}.isDeliveryFinalized`}
                                      checked={
                                        values.items[index].isDeliveryFinalized
                                      }
                                      setFieldValue={setFieldValue}
                                    ></Field>
                                  </td>
                                </tr>
                              );
                            });
                          }}
                        />
                      </tbody>
                    </table>

                    <div className="confirm-delivery--upload">
                      <InputLabel>
                        {t(
                          'Invoice(s)/ voucher(s) for the selected service lines'
                        )}
                        :
                      </InputLabel>

                      <DropZone
                        multiple={true}
                        value={attachments}
                        onSelect={files => {
                          this.handleFileUpload(files);
                        }}
                        onRemove={file => {
                          this.props.deleteAttachment(file);
                        }}
                      />
                    </div>

                    <div className="confirm-delivery--actions">
                      <GhostButton onClick={this.dismissDialog}>
                        {t('Cancel')}
                      </GhostButton>

                      <RaisedButton type="submit" disabled={isSubmitting}>
                        {t('Confirm')}
                      </RaisedButton>
                    </div>
                    {this.state.deliveryQuantitiesApprovalRequired &&
                      Confirm(
                        t('Delivery Quantity'),
                        t(
                          `Delivery quantity is greater than was responded ${this.getRequestWithExtraQuantitiesNumbersForMessage()}.  Are you sure, you want to confirm?`
                        ),
                        async () => {
                          this.confirmDeliveryWithExtraQuantities(values);
                        },
                        () => {
                          this.resetState();
                          setSubmitting(false);
                        }
                      )}
                  </form>
                );
              }}
            </Formik>
          </div>
        )}
      </Dialog>
    );
  }
}

ConfirmDeliveryModal.propTypes = {
  isOpen: PropTypes.bool,
  t: PropTypes.func,
  onClose: PropTypes.func,
  attachments: PropTypes.array,
  forSubmit: PropTypes.object,
  confirmDeliveryForSubmit: PropTypes.func,
  onConfirmDeliverSuccess: PropTypes.func,
  confirmDelivery: PropTypes.func,
  uploadAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  withCurrency: PropTypes.bool
};

export default ConfirmDeliveryModal;
